import {
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { KeyedMutator } from 'swr/_internal';
import { Add, Delete } from '@mui/icons-material';
import uuid from 'react-uuid';
import { getCellInlinePadding } from '../table/cellInlinePadding';
import { AccountData, AccountService, UpdateSharedResourcesData } from '../api';
import { FormTextField } from '../experienceSettings/formComponents/FormTextField';
import { dynamicLabelSchema } from '../experienceSettings/sections/informationSection/dynamicLabelSchema';
import { useLanguage } from '../common/GeneralUtils';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { SchedulerConfigsEditor } from './schedulerConfigs/SchedulerConfigsEditor';
import { FIXED_SLOT_MINUTES } from './schedulerConfigs/consts';
import { useApiResponseHandlerContext } from '../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../common/apiNotifications/ApiResponseHandlerContext';

type AccountSharedResourcesSettingsProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

export const AccountSharedResourcesSettings = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: AccountSharedResourcesSettingsProps) => {
  const language = useLanguage();
  const { t } = useTranslation();
  const [isUpdatingSettings, setIsUpdatingSettings] = React.useState(false);
  const [areSchedulerConfigsValid, setAreSchedulerConfigsValid] = React.useState(true);

  const { showResponseNotification } = useApiResponseHandlerContext();

  const initialValues = {
    automatic_dividable_resources: account.shared_resources?.automatic_dividable_resources,
    scheduler_configs: account.shared_resources?.scheduler_configs ?? [],
  };

  const validationSchema = yup.object().shape({
    automatic_dividable_resources: yup.array().of(
      yup.object().shape({
        id: yup.string(),
        label: yup.object().shape(dynamicLabelSchema(t, account.customer_facing_widgets_languages, true)),
        unit_capacity: yup.number().min(1),
        total_units: yup.number().min(1),
      }),
    ),
  });

  /**
   * PUT /api/account/shared-resources REPLACES the whole shared_resources object, so every save
   * must round-trip the complete payload — submitting only the section on screen silently deletes
   * the rest. Refetching first keeps the window in which someone else's change could be clobbered
   * as small as possible (spec §3.1 / §9).
   */
  const onSubmit = async (values: typeof initialValues) => {
    setIsUpdatingSettings(true);
    try {
      const latest = await updateLocalAccount();
      const latestSharedResources = (latest ?? account).shared_resources;

      const payload: UpdateSharedResourcesData = {
        automatic_dividable_resources: values.automatic_dividable_resources ?? [],
        // Slot length is fixed product-wide for now and is not editable, so it is normalised
        // here rather than trusting whatever an older config happens to hold.
        scheduler_configs: (values.scheduler_configs ?? []).map((config) => ({
          ...config,
          slot_minutes: FIXED_SLOT_MINUTES,
        })),
        // Not editable on this screen, so it is carried over exactly as stored.
        customer_choosable_resources: latestSharedResources?.customer_choosable_resources ?? [],
      };

      const updatedAccount = await AccountService.sharedResources(payload);
      await updateLocalAccount(updatedAccount);
      setIsUpdatedSnackOpen(true);
    } catch (error: any) {
      // The backend validates scheduler configs (page in two configs, bad mask length, static
      // without a value) and explains why — show that rather than a generic failure.
      const detail = error?.body?.detail;
      showResponseNotification(
        typeof detail === 'string' && detail ? detail : t('api.error.default'),
        ApiResponseHandlerType.ERROR,
      );
      console.error(error);
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ handleSubmit, values, handleChange }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <Grid container flexDirection="column">
              <Grid container flexDirection="column">
                <Grid item mb={2}>
                  <Typography variant="h4">{t('sharedResources.title')}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" color="text.secondary" mb={2}>
                    {t('sharedResources.description')}
                  </Typography>
                </Grid>
                <Grid item>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={getCellInlinePadding(0, 5)}>{t('sharedResources.name')}</TableCell>
                          <TableCell sx={getCellInlinePadding(1, 5)}>{t('sharedResources.totalUnits')}</TableCell>
                          <TableCell sx={getCellInlinePadding(2, 5)}>{t('sharedResources.unitCapacity')}</TableCell>
                          <TableCell sx={getCellInlinePadding(3, 5)}>{t('sharedResources.allocatedTo')}</TableCell>

                          <TableCell width="40px" sx={getCellInlinePadding(4, 5, true)}>
                            {t('sharedResources.actions')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.automatic_dividable_resources?.map((resource, index) => {
                          const resourceAllocationsOrderPageIds = account.experiences[0].order_pages
                            ?.map((orderPage) => {
                              const hasAllocation =
                                orderPage.shared_resources_allocations?.automatic_dividable_resources?.some(
                                  (allocation) => allocation.resource_id === resource.id,
                                );
                              return hasAllocation ? orderPage.id : null;
                            })
                            .filter(Boolean) as string[];

                          return (
                            <TableRow key={resource.id}>
                              <TableCell sx={getCellInlinePadding(0, 5)}>
                                <Grid container gap={1}>
                                  {account.customer_facing_widgets_languages.map((lang) => (
                                    <Grid item>
                                      <FormTextField
                                        width={100}
                                        label={t(`emailTemplates.${lang}`)}
                                        fieldName={`automatic_dividable_resources.${index}.label.${lang}`}
                                        isFieldUpdate
                                        disabled={false}
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </TableCell>
                              <TableCell sx={getCellInlinePadding(1, 5)}>
                                <FormTextField
                                  width={50}
                                  type="number"
                                  fieldName={`automatic_dividable_resources.${index}.total_units`}
                                  disabled={false}
                                  isFieldUpdate
                                />
                              </TableCell>
                              <TableCell sx={getCellInlinePadding(2, 5)}>
                                <FormTextField
                                  width={50}
                                  type="number"
                                  fieldName={`automatic_dividable_resources.${index}.unit_capacity`}
                                  disabled={false}
                                  isFieldUpdate
                                />
                              </TableCell>

                              <TableCell sx={getCellInlinePadding(3, 5)}>
                                <Grid container gap={1}>
                                  {resourceAllocationsOrderPageIds.map((orderPageId) => (
                                    <Grid item key={orderPageId}>
                                      <WorkshopCircle
                                        account={account}
                                        orderPageId={orderPageId}
                                        withTooltip
                                        language={language}
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </TableCell>
                              <TableCell width="40px" sx={getCellInlinePadding(4, 5, true)}>
                                <Tooltip
                                  title={
                                    resourceAllocationsOrderPageIds.length > 0
                                      ? t('sharedResources.deleteResourceTooltip')
                                      : undefined
                                  }>
                                  <span>
                                    <IconButton
                                      onClick={() => {
                                        handleChange({
                                          target: {
                                            name: 'automatic_dividable_resources',
                                            value: values.automatic_dividable_resources?.filter((_, i) => i !== index),
                                          },
                                        });
                                      }}
                                      disabled={isUpdatingSettings || resourceAllocationsOrderPageIds.length > 0}>
                                      <Delete />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <Button
                      startIcon={<Add />}
                      onClick={() => {
                        handleChange({
                          target: {
                            name: 'automatic_dividable_resources',
                            value: [
                              ...(values.automatic_dividable_resources || []),
                              {
                                id: uuid(),
                                label: { he: '' },
                                unit_capacity: 1,
                                total_units: 1,
                              },
                            ],
                          },
                        });
                      }}
                      disabled={isUpdatingSettings}
                      sx={{ m: 2 }}>
                      {t('sharedResources.addResource')}
                    </Button>
                  </TableContainer>
                </Grid>

                <Grid item>
                  <SchedulerConfigsEditor
                    account={account}
                    schedulerConfigs={values.scheduler_configs ?? []}
                    disabled={isUpdatingSettings}
                    onChange={(schedulerConfigs) =>
                      handleChange({ target: { name: 'scheduler_configs', value: schedulerConfigs } })
                    }
                    onValidityChange={setAreSchedulerConfigsValid}
                  />
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end" mt={3}>
                <Tooltip
                  title={areSchedulerConfigsValid ? '' : t('sharedResources.schedulerConfigs.fixRangesBeforeSaving')}>
                  <span>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isUpdatingSettings || !areSchedulerConfigsValid}>
                      {t('save')}
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
